import { PaymentProvider } from '../../shared/types/provider';
import { PaymentsConfig } from '../../shared/types/config';
import { PaymentsError, PaymentErrorType } from '../../shared/types/errors';
import { CheckoutParams, CheckoutSession } from '../../shared/types/checkout';
import { Customer, CustomerCreateParams } from '../../shared/types/customer';
import {
  Subscription,
  SubscriptionCreateParams,
  SubscriptionUpdateParams,
  SubscriptionStatus,
} from '../../shared/types/subscription';

export abstract class BasePaymentProvider implements PaymentProvider {
  public abstract name: string;
  protected config: PaymentsConfig;

  constructor(config: PaymentsConfig) {
    this.config = config;
  }

  public abstract initialize(): Promise<void>;
  public abstract createCheckoutSession(
    params: CheckoutParams
  ): Promise<CheckoutSession>;
  public abstract retrieveCheckoutSession(
    sessionId: string
  ): Promise<CheckoutSession>;
  public abstract createCustomer(
    params: CustomerCreateParams
  ): Promise<Customer>;
  public abstract retrieveCustomer(
    customerId: string
  ): Promise<Customer | null>;
  public abstract createSubscription(
    params: SubscriptionCreateParams
  ): Promise<Subscription>;
  public abstract cancelSubscription(subscriptionId: string): Promise<void>;
  public abstract reactivateSubscription(subscriptionId: string): Promise<void>;
  public abstract listSubscriptions(
    customerId: string,
    status?: SubscriptionStatus
  ): Promise<Subscription[]>;
  public abstract retrieveSubscription(
    subscriptionId: string
  ): Promise<Subscription | null>;
  public abstract updateSubscription(
    subscriptionId: string,
    params: SubscriptionUpdateParams
  ): Promise<Subscription>;

  protected handleError(
    error: unknown,
    type: PaymentErrorType = PaymentErrorType.UNKNOWN_ERROR
  ): PaymentsError {
    if (error instanceof PaymentsError) {
      return error;
    }

    let message = 'An unknown error occurred';
    let code: string | undefined;
    let details: Record<string, unknown> | undefined;

    if (typeof error === 'object' && error !== null) {
      if (
        'message' in error &&
        typeof (error as { message: unknown }).message === 'string'
      ) {
        message = (error as { message: string }).message;
      }
      if (
        'code' in error &&
        typeof (error as { code: unknown }).code === 'string'
      ) {
        code = (error as { code: string }).code;
      }
      details = { ...(error as Record<string, unknown>) };
    }

    return new PaymentsError(type, message, code, details);
  }
}
